(() => {
  const STORAGE_KEY = 'lova-wishlist-v2';
  const LEGACY_KEY = 'lova-wishlist-ids';
  const VIEW_KEY = 'lova-wishlist-view';
  const VALID_VIEWS = new Set(['2', '4']);

  function readRaw() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.items)) return parsed.items;
      }
    } catch {
      /* ignore */
    }

    // Migrate legacy id-only list
    try {
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (!legacy) return [];
      const ids = JSON.parse(legacy);
      if (!Array.isArray(ids)) return [];
      return ids.map((id) => ({ id: String(id) }));
    } catch {
      return [];
    }
  }

  function writeItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 2, items }));
    localStorage.setItem(LEGACY_KEY, JSON.stringify(items.map((item) => String(item.id))));
  }

  function getItems() {
    return readRaw().filter((item) => item && item.id);
  }

  function getIds() {
    return getItems().map((item) => String(item.id));
  }

  function upsertItem(item) {
    const items = getItems();
    const id = String(item.id);
    const index = items.findIndex((entry) => String(entry.id) === id);
    if (index === -1) items.push(item);
    else items[index] = { ...items[index], ...item };
    writeItems(items);
    return items;
  }

  function removeItem(productId) {
    const id = String(productId);
    const items = getItems().filter((entry) => String(entry.id) !== id);
    writeItems(items);
    return items;
  }

  function hasItem(productId) {
    return getIds().includes(String(productId));
  }

  function itemFromButton(button) {
    const card = button.closest('.lova-col-card, .lova-wish-card, [data-wishlist-card]');
    const img = card?.querySelector('img');
    const titleEl = card?.querySelector('.lova-col-card__title, .lova-wish-card__title, [data-wishlist-title]');
    const priceEl = card?.querySelector('.lova-col-card__price, .lova-wish-card__price, [data-wishlist-price]');
    const link = card?.querySelector('a[href*="/products/"]');

    return {
      id: String(button.dataset.productId || ''),
      handle: button.dataset.productHandle || '',
      variantId: button.dataset.variantId || '',
      title: button.dataset.productTitle || titleEl?.textContent?.trim() || '',
      price: button.dataset.productPrice || priceEl?.textContent?.replace(/\s+/g, ' ').trim() || '',
      image: button.dataset.productImage || img?.currentSrc || img?.getAttribute('src') || '',
      url: button.dataset.productUrl || link?.getAttribute('href') || '',
    };
  }

  function syncButtons(root = document) {
    const ids = new Set(getIds());
    root.querySelectorAll('[data-wishlist-trigger]').forEach((button) => {
      const id = String(button.dataset.productId || '');
      const active = ids.has(id);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const title =
        button.dataset.productTitle ||
        button.closest('.lova-col-card, .lova-wish-card')?.querySelector('.lova-col-card__title, .lova-wish-card__title')
          ?.textContent?.trim() ||
        'product';
      button.setAttribute(
        'aria-label',
        active ? `Remove ${title} from wishlist` : `Add ${title} to wishlist`
      );
    });
  }

  function syncHeaderBadge() {
    const count = getItems().length;
    document.querySelectorAll('[data-lova-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
      el.toggleAttribute('data-empty', count === 0);
    });
    document.querySelectorAll('[data-lova-wishlist-link]').forEach((el) => {
      el.setAttribute('aria-label', count ? `Wishlist, ${count} items` : 'Wishlist');
    });
  }

  function emitChange(detail) {
    document.dispatchEvent(new CustomEvent('lova:wishlist:change', { bubbles: true, detail }));
    document.dispatchEvent(
      new CustomEvent('lova:wishlist:toggle', {
        bubbles: true,
        detail,
      })
    );
  }

  function toggleFromButton(button) {
    const productId = String(button.dataset.productId || '');
    if (!productId) return;

    const added = !hasItem(productId);
    let items;
    if (added) {
      items = upsertItem(itemFromButton(button));
    } else {
      items = removeItem(productId);
    }

    syncButtons();
    syncHeaderBadge();
    emitChange({
      added,
      productId,
      productHandle: button.dataset.productHandle || '',
      variantId: button.dataset.variantId || '',
      ids: items.map((item) => String(item.id)),
      items,
      button,
    });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cardMarkup(item) {
    const title = escapeHtml(item.title || 'Product');
    const price = escapeHtml(item.price || '');
    const url = escapeHtml(item.url || `/products/${item.handle || ''}`);
    const image = escapeHtml(item.image || '');
    const id = escapeHtml(item.id);
    const handle = escapeHtml(item.handle || '');
    const variantId = escapeHtml(item.variantId || '');

    return `
      <li class="lova-col-grid__item">
        <article class="lova-wish-card lova-col-card" data-wishlist-card data-product-id="${id}">
          <div class="lova-wish-card__media lova-col-card__media">
            <a class="lova-wish-card__media-link lova-col-card__media-link" href="${url}" tabindex="-1" aria-hidden="true">
              ${
                image
                  ? `<img class="lova-wish-card__img lova-col-card__img" src="${image}" alt="${title}" loading="lazy" width="800" height="1067">`
                  : `<span class="lova-wish-card__placeholder" aria-hidden="true"></span>`
              }
            </a>
            <button
              type="button"
              class="lova-wish-card__remove"
              data-wishlist-remove
              data-product-id="${id}"
              data-product-handle="${handle}"
              data-variant-id="${variantId}"
              aria-label="Remove ${title} from wishlist"
            >
              <span class="lova-wish-card__remove-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
                  <path d="M9.5 4.75h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M5.75 7.25h12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M9 10.25v6.5M12 10.25v6.5M15 10.25v6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M7.5 7.25l.7 11.1a1.5 1.5 0 0 0 1.5 1.4h4.6a1.5 1.5 0 0 0 1.5-1.4l.7-11.1" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
          <a class="lova-wish-card__meta-link lova-col-card__meta-link" href="${url}">
            <div class="lova-wish-card__meta lova-col-card__meta">
              <h3 class="lova-wish-card__title lova-col-card__title" data-wishlist-title>${title}</h3>
              <p class="lova-wish-card__price lova-col-card__price" data-wishlist-price>${price}</p>
            </div>
          </a>
        </article>
      </li>
    `;
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    const amount = (Number(cents) / 100).toFixed(2);
    return `Rs.${amount}`;
  }

  async function hydrateItem(item) {
    if (!item.handle) return item;
    try {
      const res = await fetch(`/products/${item.handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) return item;
      const product = await res.json();
      const variant =
        (item.variantId && product.variants?.find((v) => String(v.id) === String(item.variantId))) ||
        product.variants?.[0];
      let image = item.image;
      if (typeof product.featured_image === 'string' && product.featured_image) {
        image = product.featured_image;
      } else if (Array.isArray(product.images) && product.images[0]) {
        image = typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src;
      }
      const priceCents = variant?.price ?? product.price;
      return {
        ...item,
        title: product.title || item.title,
        url: `/products/${product.handle}`,
        handle: product.handle || item.handle,
        image,
        price: priceCents != null ? formatMoney(priceCents) : item.price,
        variantId: String(variant?.id || item.variantId || ''),
      };
    } catch {
      return item;
    }
  }

  class LovaWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-lova-wish-grid]');
      this.empty = this.querySelector('[data-lova-wish-empty]');
      this.countEl = this.querySelector('[data-lova-wish-page-count]');
      this.inputs = this.querySelectorAll('input[name="lova-wish-view"]');
      const stored = sessionStorage.getItem(VIEW_KEY);
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      let initial = VALID_VIEWS.has(stored) ? stored : this.dataset.defaultView || '4';
      if (isMobile) initial = '2';
      else if (!VALID_VIEWS.has(initial)) initial = '4';
      this.applyView(initial);
      this.render();
      this.addEventListener('change', this);
      this.addEventListener('click', this);
      document.addEventListener('lova:wishlist:change', this);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this);
      this.removeEventListener('click', this);
      document.removeEventListener('lova:wishlist:change', this);
    }

    handleEvent(event) {
      if (event.type === 'lova:wishlist:change') {
        this.render();
        return;
      }

      if (event.type === 'change') {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.name !== 'lova-wish-view') return;
        this.applyView(target.value);
        sessionStorage.setItem(VIEW_KEY, target.value);
        return;
      }

      if (event.type === 'click') {
        const remove = event.target.closest('[data-wishlist-remove]');
        if (!remove || !this.contains(remove)) return;
        event.preventDefault();
        event.stopPropagation();
        const productId = String(remove.dataset.productId || '');
        if (!productId) return;
        const items = removeItem(productId);
        syncButtons();
        syncHeaderBadge();
        emitChange({
          added: false,
          productId,
          productHandle: remove.dataset.productHandle || '',
          variantId: remove.dataset.variantId || '',
          ids: items.map((item) => String(item.id)),
          items,
          button: remove,
        });
      }
    }

    applyView(value) {
      const view = VALID_VIEWS.has(value) ? value : '4';
      if (this.grid) this.grid.dataset.view = view;
      this.inputs.forEach((input) => {
        input.checked = input.value === view;
      });
    }

    async render() {
      const items = getItems();
      if (this.countEl) this.countEl.textContent = String(items.length);

      if (!items.length) {
        if (this.grid) this.grid.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        this.dataset.empty = 'true';
        return;
      }

      if (this.empty) this.empty.hidden = true;
      this.dataset.empty = 'false';

      if (this.grid) {
        this.grid.innerHTML = items.map(cardMarkup).join('');
      }

      // Refresh titles/prices/images from Shopify when possible
      const hydrated = await Promise.all(items.map(hydrateItem));
      writeItems(hydrated);
      if (this.grid) {
        this.grid.innerHTML = hydrated.map(cardMarkup).join('');
      }
      syncHeaderBadge();
    }
  }

  // Global heart toggles (collection cards, product cards, etc.)
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-wishlist-trigger]');
      if (!trigger) return;
      // Wishlist page uses trash remove, not heart toggle
      if (trigger.matches('[data-wishlist-remove]')) return;
      event.preventDefault();
      event.stopPropagation();
      toggleFromButton(trigger);
    },
    true
  );

  document.addEventListener('lova:wishlist:change', () => {
    syncButtons();
    syncHeaderBadge();
  });

  if (!customElements.get('lova-wishlist-page')) {
    customElements.define('lova-wishlist-page', LovaWishlistPage);
  }

  window.LovaWishlist = {
    getItems,
    getIds,
    hasItem,
    upsertItem,
    removeItem,
    syncButtons,
    syncHeaderBadge,
    toggleFromButton,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncButtons();
      syncHeaderBadge();
    });
  } else {
    syncButtons();
    syncHeaderBadge();
  }
})();
